import React, { useContext, useEffect, useState } from 'react'

import {
  getDateRangeDefaults,
  HintToggleButton,
  If,
  Loader,
} from '@paytrace/pt-skel/Component'
import { AppCtx } from '@paytrace/pt-skel/Context'
import { getSubmittedData } from '@paytrace/pt-skel/Helper'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import Event from '../Event'
import {
  BatchTransactionErrorFeedback,
  BatchTransactionFeedback,
  SemiIntegratedTransactionSearchResult,
  TransactionSearch,
  TransactionSearchResult,
} from '../Component'

interface Props {
  action: string
  helpLink: string
  searchType: string
  title: string
}

const LIMIT = 50
const self = Symbol('BaseBatchTransactions')

export function BaseBatchTransactions (props: Props) {
  const context = useContext(AppCtx)
  const dateDefaults = getDateRangeDefaults()

  const [rows, setRows] = useState<{ auth_method: string }[]>([])
  const [paxEmvRows, setPaxEmvRows] = useState<{ auth_method: string }[]>([])
  const [showPax, setShowPax] = useState(true)
  const [startDate, setStartDate] = useState(dateDefaults.startDate)
  const [startTime, setStartTime] = useState(dateDefaults.startTime)
  const [endDate, setEndDate] = useState(dateDefaults.endDate)
  const [endTime, setEndTime] = useState(dateDefaults.endTime)
  const [isLoading, setIsLoading] = useState(false)
  const [offset, setOffset] = useState(0)
  const [rowCount, setRowCount] = useState(0)
  const [hidePagination, setHidePagination] = useState(true)
  const [orderId, setOrderId] = useState('')
  const [searchText, setSearchText] = useState('')

  useEffect(() => {
    PubSub.sub(self, Event.SEARCH_TRANSACTIONS_RESPONSE, (json: { auth_method: string }[]) => {
      setRows(json.filter(row => row.auth_method !== 'paxemv'))
      setPaxEmvRows(json.filter(row => row.auth_method === 'paxemv'))
      setIsLoading(false)
      setHidePagination(false)

      // Check the count for pagination purposes
      if (Array.isArray(json)) {
        setRowCount(json.length)
      }
    })

    return () => {
      PubSub.unsub(self)
    }
  }, [props.action, props.searchType, isLoading, hidePagination])

  useEffect(() => {
    PubSub.sub(self, Event.BATCH_TRANSACTIONS_OP_REQUEST, (_json) => {
      setHidePagination(true)
    })
  }, [props.action, props.searchType, hidePagination])

  // NOTE: We are shadowing the offset var - if we try to change it in the
  // pagination functions, it won't take effect until the next re-render
  // This also helps, because the standard submit doesn't include the current
  // offset, which ensures pagination is cleared on new searches from main form.
  function doRequest (formData: {}, offset = 0) {
    setIsLoading(true)
    setOffset(offset)

    // Clear some previous UI feedback
    PubSub.pub('VALIDATION_ERROR::clear')
    PubSub.pub(Event.BATCH_TRANSACTION_ERROR_FEEDBACK, { records: {}, response: [] })
    PubSub.pub(Event.BATCH_TRANSACTION_FEEDBACK, { records: {}, response: [] })

    PubSub.pub(
      Event.SEARCH_TRANSACTIONS_REQUEST,
      {
        ...formData,
        searchType: props.searchType,
        paytraceId: Number(context.paytraceId),
        startDate,
        startTime,
        endDate,
        endTime,
        limit: LIMIT,
        offset,
        orderId,
        searchText,
      }
    )
  }

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()

    // TODO: properly map to the request
    const formData = getSubmittedData(e)
    doRequest(formData)
    return false
  }

  function prevPage () {
    doRequest({}, offset - LIMIT)
  }

  function nextPage () {
    doRequest({}, offset + LIMIT)
  }

  function renderPagination () {
    if (isLoading || hidePagination) {
      return (<React.Fragment />)
    }

    // Currently we don't have a way to get the total, so we approximate based
    // on having a full result set equal to our limit
    const hasMore = rowCount === LIMIT
    const buttons = []
    const style = {
      fontSize: '20px',
      padding: '0px',
      width: '40px',
    }

    if (offset > 0) {
      buttons.push((<button key={1} onClick={prevPage} style={style}>&laquo;</button>))
    } else {
      buttons.push((<button key={1} style={style}>&bull;</button>))
    }

    buttons.push(<span key={0}>Page {(offset / LIMIT) + 1}</span>,)

    if (hasMore) {
      buttons.push((<button key={2} onClick={nextPage} style={style}>&raquo;</button>))
    } else {
      buttons.push((<button key={2} style={style}>&bull;</button>))
    }

    return buttons.length > 1 ? (<div>{buttons}</div>) : <React.Fragment />
  }

  function renderSearch () {
    if (isLoading) {
      return (<Loader />)
    }

    // If we hvae some, show the tabular view
    if (paxEmvRows.length > 0 && props.action === 'void') {
      return (
        <div style={{ marginTop: '20px' }}>
          <div>
            <span
              style={{
                border: '1px solid #ccc',
                borderBottom: '0px',
                background: showPax ? '#eee' : '#fff',
                color: showPax ? '#999' : '#000',
                cursor: 'pointer',
                fontWeight: showPax ? 'normal' : 'bold',
                padding: '9px',
                paddingBottom: '3px',
                paddingTop: showPax ? '9px' : '11px',
                borderRadius: '10px 10px 0px 0px',
              }}
              onClick={_ => setShowPax(false)}>Standard ({rows.length})</span>
            <span
              style={{
                border: '1px solid #ccc',
                borderBottom: '0px',
                background: showPax ? '#fff' : '#eee',
                color: showPax ? '#000' : '#999',
                cursor: 'pointer',
                fontWeight: showPax ? 'bold' : 'normal',
                padding: '9px',
                paddingBottom: '3px',
                paddingTop: showPax ? '11px' : '9px',
                boxShadow: '3px',
                borderRadius: '10px 10px 0px 0px',
              }}
              onClick={_ => setShowPax(true)}>PAX EMV ({paxEmvRows.length})</span>
          </div>
          <If is={showPax}>
            <SemiIntegratedTransactionSearchResult
              action={props.action}
              onSubmit={console.debug}
              rows={paxEmvRows} />
          </If>
          <If isNot={showPax}>
            <TransactionSearchResult
              action={props.action}
              onSubmit={console.debug}
              rows={rows} />
          </If>
        </div>
      )
    }

    // Traditional render will always occur unless we have paxemv
    return (
      <TransactionSearchResult
        action={props.action}
        onSubmit={console.debug}
        rows={rows} />
    )
  }

  return (
    <>
      <div className='float-right'>
        <HintToggleButton />
        <a className='help-link' href={props.helpLink}>Help</a>
      </div>
      <h1>{props.title}</h1>
      <hr className='clear' />
      <BatchTransactionFeedback action={props.action} merchantId={Number(context.merchant)}/>
      <BatchTransactionErrorFeedback action={props.action} />
      <TransactionSearch
        setStartDate={setStartDate}
        setStartTime={setStartTime}
        setEndDate={setEndDate}
        setEndTime={setEndTime}
        startDate={startDate}
        endDate={endDate}
        startTime={startTime}
        endTime={endTime}
        rowCount={rowCount}
        setOrderId={setOrderId}
        setSearchText={setSearchText}
        onSubmit={submit} />
      {renderPagination()}
      {renderSearch()}
      {renderPagination()}
    </>
  )
}

export default BaseBatchTransactions
